import { LoaderCircle } from 'lucide-react';
import { FC, ReactNode, useMemo } from 'react';
import { Link } from 'react-router';

import { cn } from '../utils/class-names.util.ts';

import { CommonGlossyEffect } from './common-glossy-effect.tsx';

interface Props {
  children: ReactNode;
  fullWidth?: boolean;
  className?: string;
  glossyEffectClassName?: string;
  disabled?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  asLink?: boolean;
  to?: string;
  replace?: boolean;
  type?: 'button' | 'submit' | 'reset';
  handleTransitionEnd?: () => void;
}

export const CommonButton: FC<Props> = ({
  children,
  isLoading,
  fullWidth,
  asLink,
  to,
  className,
  glossyEffectClassName,
  disabled,
  onClick,
  replace,
  type = 'button',
  handleTransitionEnd,
}: Props) => {
  const buttonClassName = cn(
    'text-primary-contrast align-center font-dyslexic relative block flex h-16 justify-center rounded-lg px-4 py-4 transition-colors',
    fullWidth ? 'w-full' : '',
    disabled || isLoading
      ? 'bg-disabled hover:bg-disabled cursor-not-allowed'
      : 'bg-primary hover:bg-primary-hover active:bg-primary-active',
    className
  );

  const buttonGlossyEffectClassName = glossyEffectClassName ? glossyEffectClassName : 'rounded-lg';

  const content = useMemo(() => {
    return (
      <>
        <CommonGlossyEffect className={buttonGlossyEffectClassName} />
        {isLoading ? (
          <LoaderCircle className="text-foreground-soft relative z-10 m-auto h-5 w-5 animate-spin" />
        ) : (
          <div className="relative z-10">{children}</div>
        )}
      </>
    );
  }, [isLoading, children, buttonGlossyEffectClassName]);

  if (asLink && !disabled && !isLoading) {
    return (
      <Link
        to={to ?? ''}
        className={buttonClassName}
        replace={replace}
        onClick={onClick}
        onTransitionEnd={handleTransitionEnd}>
        {content}
      </Link>
    );
  }

  return (
    <button
      disabled={disabled || isLoading}
      className={buttonClassName}
      onClick={onClick}
      type={type}
      onTransitionEnd={handleTransitionEnd}>
      {content}
    </button>
  );
};
